<template>
  <content-container title="发票完税" :table-row-count="tableData.length" style="height: calc(100% - 76px);">
    <!-- 搜索条件区域 -->
    <search-container>
      <search-col>
        <el-input v-model="params.businessNo" placeholder="业务单号" clearable @keydown="onEnter" />
      </search-col>
      <search-col>
        <el-select
          v-model="params.status"
          placeholder="完税状态"
          clearable
        >
          <el-option
            v-for="v in statusOptions"
            :key="v.value"
            :label="v.label"
            :value="v.value"
          />
        </el-select>
      </search-col>
      <search-col>
        <el-input v-model="params.invoiceSubName" placeholder="发票科目" clearable @keydown="onEnter" />
      </search-col>
      <template v-if="source === 'sass-match'">
        <search-col>
          <el-input v-model="params.companyName" placeholder="企业名称" clearable @keydown="onEnter" />
        </search-col>
        <search-col>
          <el-input v-model="params.agentName" placeholder="直属代理商" clearable @keydown="onEnter" />
        </search-col>
        <search-col>
          <el-input v-model="params.topAgentName" placeholder="所属特代" clearable @keydown="onEnter" />
        </search-col>
      </template>

      <search-col>
        <el-date-picker
          v-model="dateRange"
          style="width: 100%;"
          type="daterange"
          :disabled-date="disabledDate"
          start-placeholder="支付时间(起)"
          :default-time="defaultTimeFormat"
          format="YYYY-MM-DD"
          value-format="YYYY-MM-DD HH:mm:ss"
          end-placeholder="支付时间(止)"
          @keydown.enter="doSearch"
        />
      </search-col>
      <search-col>
        <el-date-picker
          v-model="invoiceDateRange"
          style="width: 100%;"
          type="daterange"
          :disabled-date="disabledDate"
          start-placeholder="开具时间(起)"
          :default-time="defaultTimeFormat"
          format="YYYY-MM-DD"
          value-format="YYYY-MM-DD HH:mm:ss"
          end-placeholder="开具时间(止)"
          @keydown.enter="doSearch"
        />
      </search-col>

      <template v-if="source === 'sass-crowd'">
        <search-col>
          <el-input v-model="params.serverName" placeholder="服务主体" clearable @keydown="onEnter" />
        </search-col>
      </template>

      <template #buttons>
        <el-button @click="resetForm">
          <svg-icon icon="tabler:refresh" />重置
        </el-button>
        <el-button type="primary" @click="doSearch">
          <svg-icon icon="lucide:search" />查询
        </el-button>
      </template>
    </search-container>

    <!-- 非搜索区域的额外按钮 -->
    <div v-if="source !== 'server'" class="export-box">
      <el-button type="primary">
        <div class="download-icon" @click="handleExport">
          导出
          <el-tooltip content="导出当前筛选条件下全部列表数据（Excel格式）">
            <svg-icon icon="mingcute:information-line" class="!w-4 !h-4 cursor-pointer" />
          </el-tooltip>
        </div>
      </el-button>
      <el-button type="primary">
        <div class="download-icon" @click="handleDownInvoice">
          下载发票
          <el-tooltip content="在下载中心导出当前筛选条件下已开具的全部完税发票">
            <svg-icon icon="mingcute:information-line" class="!w-4 !h-4 cursor-pointer" />
          </el-tooltip>
        </div>
      </el-button>
      <el-button type="primary">
        <div class="download-icon" @click="handleTaxPay">
          下载完税证明
          <el-tooltip content="在下载中心导出当前筛选条件下已开具的全部完税证明">
            <svg-icon icon="mingcute:information-line" class="!w-4 !h-4 cursor-pointer" />
          </el-tooltip>
        </div>
      </el-button>
    </div>

    <!-- 表格数据 -->
    <el-table v-loading="loading" class="normal-table" :border="true" :scrollbar-always-on="true" :data="tableData">
      <el-table-column align="left" type="index" label="序号" :min-width="80" />
      <el-table-column align="left" prop="invoiceSubName" show-overflow-tooltip label="完税发票科目" :min-width="180" />
      <el-table-column align="right" prop="invoiceAmount" label="发票金额" :min-width="150" />
      <el-table-column align="right" prop="successCount" label="申请成功订单数" :formatter="(row) => isNull(row.successCount) ? '--' : row.successCount" :min-width="200" />
      <el-table-column align="right" prop="errorCount" label="申请失败订单数" :formatter="(row) => isNull(row.errorCount) ? '--' : row.errorCount" :min-width="200" />
      <el-table-column v-if="source !== 'company' && source !== 'server'" align="right" show-overflow-tooltip prop="companyTaxAmount" label="企业支付税金（元）" :min-width="180">
        <template #header>
          <div class="flex items-center">
            企业支付税金（元）
            <el-tooltip
              effect="dark"
              placement="top"
              content="企业实际在系统内支付的此完税发票税金合计"
            >
              <svg-icon icon="mingcute:information-line" class="!w-4 !h-4 cursor-pointer" />
            </el-tooltip>
          </div>
        </template>
        <template #default="scope">
          <span
            v-if="!isNull(scope.row.companyTaxAmount)"
            :class="scope.row.companyTaxAmount !== scope.row.taxAmount && !isNull(scope.row.taxAmount) ?
              'text-[var(--color-danger)]' : ''"
          >
            {{ scope.row.companyTaxAmount }}
          </span>
          <span v-else>
            --
          </span>
        </template>
      </el-table-column>
      <el-table-column align="right" show-overflow-tooltip prop="taxAmount" label="税金（元）" :formatter="(row) => isNull(row.taxAmount) ? '--' : row.taxAmount" :min-width="120" />
      <el-table-column align="center" prop="status" label="完税状态" :min-width="120">
        <template #default="scope">
          <custom-tag
            :label="invoiceApplyStatusLabel[scope.row.status]"
            :type="invoiceApplyStatusStyle[scope.row.status]"
            :content="scope.row.status === EInvoiceApplyStatus.InvoiceFailed ? '开票失败，具体情况咨询开发解决' : scope.row.status === EInvoiceApplyStatus.Rejected ? scope.row.approveMessage : ''"
          />
        </template>
      </el-table-column>
      <template v-if="source === 'sass-match'">
        <el-table-column align="left" show-overflow-tooltip prop="companyName" label="企业名称" :min-width="260" />
        <el-table-column align="left" show-overflow-tooltip prop="agentName" label="直属代理商" :min-width="260" :formatter="(row) => isNull(row.agentName) ? '--' : row.agentName" />
        <el-table-column align="left" show-overflow-tooltip prop="topAgentName" label="所属特代" :min-width="260" />
      </template>
      <el-table-column v-if="isCrowd" align="left" show-overflow-tooltip prop="serverName" label="服务主体" :min-width="200" />
      <el-table-column v-if="source !== 'company'" align="left" show-overflow-tooltip prop="payTime" label="税金支付时间" :formatter="(row) => isNull(row.payTime) ? '--' : row.payTime" :min-width="180" />
      <el-table-column align="left" show-overflow-tooltip prop="invoiceTime" label="发票开具时间" :formatter="(row) => isNull(row.invoiceTime) ? '--' : row.invoiceTime" :min-width="180" />
      <el-table-column align="left" prop="businessNo" show-overflow-tooltip label="业务单号" :min-width="260" />
      <el-table-column
        align="center"
        prop="actions"
        fixed="right"
        label="操作"
        :min-width="225"
      >
        <template #default="scope">
          <div v-if="scope.row.status !== EInvoiceApplyStatus.TaxAudit" class="flex justify-center">
            <el-button type="primary" link @click="handleDetail(scope.row)">
              详情
            </el-button>
            <el-button v-if="scope.row.status === EInvoiceApplyStatus.PendingPayment" v-permission="[`${isCrowd ? 'crowd' : 'broker'}:invoice:tax:pay`]" type="primary" link @click="handlePay(scope.row)">
              支付个税
            </el-button>
            <template v-if="scope.row.status === EInvoiceApplyStatus.Invoiced">
              <el-button type="primary" link @click="hanldeDownloadInvoice(scope.row)">
                下载发票
              </el-button>
              <el-button type="primary" link @click="hanldeDownload(scope.row)">
                下载完税证明
              </el-button>
            </template>
          </div>
          <div v-else class="flex justify-center">
            --
          </div>
        </template>
      </el-table-column>
    </el-table>

    <!-- 底部分页 -->
    <template #footer>
      <el-pagination
        v-model:current-page="params.page"
        v-model:page-size="params.size"
        :page-sizes="[defaultPageSize, 25, 50, 100]"
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
        background
        @size-change="onSizeChange"
        @current-change="onCurrentPageChange"
      />
    </template>

    <!-- 支付弹窗 -->
    <invoice-pay
      :visible="showInvoicePay"
      :row="currentRow"
      @close="showInvoicePay = false"
      @confirm="fetchData"
    />
  </content-container>
</template>

<script setup lang="ts">
import InvoicePay from '../dialogs/InvoicePay.vue';
import { reactive, ref, PropType, computed, watch } from 'vue';
import { ElMessage } from 'element-plus';
import { SvgIcon, CustomTag } from '@vue3/components';
import {
  ContentContainer,
  SearchContainer,
  SearchCol,
  queryInvoiceApplyPage,
  queryInvoiceReceiptUrl,
  getCustomState,
  queryMaintenanceStatus,
  queryInvoiceApplyExport,
  queryExportTaxInvoices,
  queryExportTaxCertificates,
} from '@core/main';
import { EHnInvoiceType, IEnterpriseItem, IServerOptionsItem } from '@core/main/types/common';
import {
  EInvoiceApplyStatus,
  IHnInvoiceApplyItem,
  IInvoiceApplyPageParams,
  invoiceApplyStatusLabel,
  invoiceApplyStatusStyle,
} from '@core/main/types/crowd-source/invoice-pay-taxes';
import { downloadFile, getFomattedTimeRange, isNull } from '@core/tools';
import { useRouter } from 'vue-router';

defineOptions({ name: 'InvoiceTaxPayment' }); // 众包灵工 - 发票完税

const props = defineProps({
  source: {
    type: String as PropType<'server' | 'company' | 'sass-crowd' | 'sass-match'>,
    default: 'server',
  },
  companyList: {
    type: Array as PropType<IEnterpriseItem[]>,
    default: () => [],
  },
  serverList: {
    type: Array as PropType<IServerOptionsItem[]>,
    default: () => [],
  },
  isPermission: {
    type: Boolean,
    default: false,
  },
});

const router = useRouter();

const defaultPageSize = 12;

const state = getCustomState<{ businessNo: string }>();

const params = reactive<IInvoiceApplyPageParams>({
  page: 1,
  size: defaultPageSize,
  businessNo: state.businessNo,
  businessType: props.source === 'sass-match' || props.source === 'company' ? 2 : props.source === 'sass-crowd' || props.source === 'server' ? 3 : undefined,
});
const statusOptions = ref(
  Object.keys(invoiceApplyStatusLabel).map((key) => ({
    label: invoiceApplyStatusLabel[key],
    value: Number(key),
  }))
    .filter((v) => props.source === 'company' ? v.value !== EInvoiceApplyStatus.InternalAudit && v.value !== EInvoiceApplyStatus.TaxAudit && v.value !== EInvoiceApplyStatus.PendingPayment : true),
);
const loading = ref(false);
const total = ref(0);
const tableData = ref([] as IHnInvoiceApplyItem[]);
const showInvoicePay = ref(false);
const currentRow = ref<IHnInvoiceApplyItem>();
const dateRange = ref<string[]>([]);
const invoiceDateRange = ref<string[]>([]);
const disabledDate = (current: Date) => current.getTime() > Date.now();

const defaultTimeFormat = computed(() => getFomattedTimeRange());
const isCrowd = computed(() => props.source === 'sass-crowd' || props.source === 'server');

async function getMaintenanceStatus() {
  try {
    await queryMaintenanceStatus();
    return true;
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
}

async function handlePay(row: IHnInvoiceApplyItem) {
  if (!await getMaintenanceStatus()) return;

  currentRow.value = row;
  showInvoicePay.value = true;
}

async function fetchData() {
  loading.value = true;

  try {
    const { data, total: all } = await queryInvoiceApplyPage({
      ...params,
      payTimeStart: isNull(dateRange.value?.[0]) ? undefined : dateRange.value[0],
      payTimeEnd: isNull(dateRange.value?.[1]) ? undefined : dateRange.value[1],
      invoiceTimeStart: isNull(invoiceDateRange.value?.[0]) ? undefined : invoiceDateRange.value[0],
      invoiceTimeEnd: isNull(invoiceDateRange.value?.[1]) ? undefined : invoiceDateRange.value[1],
    });

    tableData.value = data;
    total.value = all;
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }

  loading.value = false;
}

function handleDetail(row: IHnInvoiceApplyItem) {
  router.push({
    name: isCrowd.value ? 'CrowdSourceOrderDetail' : 'BrokerOrderDetail',
    query: {
      id: row.id,
      source: props.source,
    },
    state: JSON.parse(JSON.stringify(row)),
  });
}

async function handleExport() {
  try {
    const req = {
      ...JSON.parse(JSON.stringify(params)),
      payTimeStart: isNull(dateRange.value[0]) ? undefined : dateRange.value[0],
      payTimeEnd: isNull(dateRange.value[1]) ? undefined : dateRange.value[1],
      hnInvoiceType: isCrowd.value ? EHnInvoiceType.ONLINE : undefined,
    };

    delete req.page;
    delete req.size;

    const { buffer } = await queryInvoiceApplyExport(req);

    downloadFile(buffer, '', 'xlsx');
    ElMessage.success('导出成功');
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
}

async function handleDownInvoice() {
  try {
    const req = {
      ...JSON.parse(JSON.stringify(params)),
      payTimeStart: isNull(dateRange.value?.[0]) ? undefined : dateRange.value[0],
      payTimeEnd: isNull(dateRange.value?.[1]) ? undefined : dateRange.value[1],
      businessId: props.source === 'server' ? props.serverList?.[0]?.id : props.source === 'company' ? props.companyList?.[0]?.id : undefined,
      businessType: params.businessType,
    };

    delete req.page;
    delete req.size;

    await queryExportTaxInvoices(req, isCrowd.value);

    ElMessage.success('发票下载申请成功，请稍后在右上角的‘下载中心’中查看');
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
}

async function handleTaxPay() {
  try {
    const req = {
      ...JSON.parse(JSON.stringify(params)),
      payTimeStart: isNull(dateRange.value[0]) ? undefined : dateRange.value[0],
      payTimeEnd: isNull(dateRange.value[1]) ? undefined : dateRange.value[1],
      businessId: props.source === 'server' ? props.serverList?.[0]?.id : props.source === 'company' ? props.companyList?.[0]?.id : undefined,
      businessType: params.businessType,
    };

    delete req.page;
    delete req.size;

    await queryExportTaxCertificates(req, isCrowd.value);

    ElMessage.success('完税证明下载申请成功，请稍后在右上角的‘下载中心’中查看');
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
}

async function hanldeDownload(row: IHnInvoiceApplyItem) {
  try {
    const res = await queryInvoiceReceiptUrl(row.id);

    if (res) {
      window.open(res, '_blank');
      ElMessage.success('下载完税证明成功');
    }
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
}

function hanldeDownloadInvoice(row: IHnInvoiceApplyItem) {
  if (isNull(row?.invoiceFileUrl)) {
    ElMessage.error('生成发票大约需要10分钟，请稍后再试');
  } else {
    window.open(row?.invoiceFileUrl, '_blank');
    ElMessage.success('下载发票成功');
  }
}

function resetForm() {
  dateRange.value = [];
  invoiceDateRange.value = [];

  for (const key in params) {
    if (!['page', 'size', 'businessType'].includes(key)) {
      params[key] = undefined;
    }
  }
}

function onSizeChange(size: number) {
  params.size = size;
  params.page = 1;
  fetchData();
}

function onCurrentPageChange(page: number) {
  params.page = page;
  fetchData();
}

function doSearch() {
  params.page = 1;
  fetchData();
}

function onEnter(e) {
  if (e.keyCode === 13) doSearch();
}

watch(
  () => props.isPermission,
  (val) => {
    if (!val) {
      fetchData();
    }
  },
  { immediate: true },
);
</script>

<style scoped lang="scss">
.export-box {
  @apply mb-[20px];

  :deep(.el-button) {
    @apply px-2;
  }
}

.download-icon {
  @apply flex items-center gap-x-1;
}
</style>
